import { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'antd';
import { MessageOutlined, PlusOutlined, RobotOutlined, SearchOutlined } from '@ant-design/icons';

import { PageHeader } from '../../../components/shared';
import { Badge, Button, Empty, Input, Skeleton } from '../../../components/ui';
import { boDau } from '../../../components/layout/nav-search';
import { useDuongDan } from '../../../hooks/useDuongDan';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { soVN } from '../../../lib/dinh-dang';
import { useToast } from '../../../lib/toast';
import { HopXacNhan } from '../components/chung/HopXacNhan';
import { DaiLoi } from '../components/chung/VungDuLieu';
import { FormBot } from '../components/bot/FormBot';
import { KHOI } from '../components/bot/chung';
import { QUYEN } from '../constants';
import { useBotQuanTri, useXoaBot } from '../hooks/useBot';
import { useBoTriThuc, useDanhSachNguon } from '../hooks/useChung';
import { trangThaiDongBot } from '../nghiep-vu/cau-hinh-bot';
import type { Chatbot } from '../types';

/**
 * Cấu hình chatbot AI (`/client/tro-ly-ai/chatbot`), port `ChatbotConfigView` của native.
 *
 * Một bot là một BẢN KHAI, không phải một tiến trình: màn này ghi lại bot nói giọng nào, tra ở đâu, câu nào đã có sẵn câu
 * trả lời — trợ lý đọc bản khai ấy khi có người hỏi. Tài liệu và số liệu nằm cạnh nhau trong một mục vì chúng là hai nguồn
 * của CÙNG một câu trả lời; tách hai mục là dạy người dùng phải chọn một.
 */
export function ChatbotConfigView() {
  const { t } = useT();
  const toast = useToast();
  const q = useModulePermissions(QUYEN);
  const [chon, setChon] = useState<string | null>(null);
  const [taoMoi, setTaoMoi] = useState(false);
  const [tuKhoa, setTuKhoa] = useState('');
  const [xoaBot, setXoaBot] = useState<Chatbot | null>(null);
  /** Việc đang chờ người dùng xác nhận bỏ bản nháp. */
  const [choBo, setChoBo] = useState<(() => void) | null>(null);
  // Ref chứ không state: chỉ đọc lúc bấm chuyển bot, không cần vẽ lại cả danh sách mỗi phím gõ.
  const banRef = useRef(false);
  const datBan = useCallback((ban: boolean) => {
    banRef.current = ban;
  }, []);

  const ds = useBotQuanTri();
  const bo = useBoTriThuc();
  const nguon = useDanhSachNguon();
  const xoa = useXoaBot();

  const suaDuoc = chon || !taoMoi ? q.chatbotSua : q.chatbotTao;
  const dangSua = ds.data?.find((b) => b.id === chon) ?? null;
  const dsLoc = useMemo(() => {
    const k = boDau(tuKhoa.trim());
    return k ? (ds.data ?? []).filter((b) => boDau(b.name).includes(k)) : (ds.data ?? []);
  }, [ds.data, tuKhoa]);

  /** Rời bản đang sửa dở thì hỏi trước — chuyển đi là mất thay đổi chưa lưu. */
  const roiForm = (lam: () => void) => {
    if (!banRef.current) return lam();
    setChoBo(() => lam);
  };

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.troLiAi') }, { label: t('nav.muc.cauHinhChatbot') }]}
          titleIcon={<RobotOutlined className="text-primary-600" />}
          title={t('troLyAiBot.tieuDe')}
          subtitle={t('troLyAiBot.phuDe')}
          actions={
            q.chatbotTao ? (
              <Button
                size="md"
                variant="primary"
                icon={<PlusOutlined />}
                onClick={() =>
                  roiForm(() => {
                    setChon(null);
                    setTaoMoi(true);
                  })
                }
              >
                {t('troLyAiBot.themChatbot')}
              </Button>
            ) : null
          }
        />

        {ds.error ? (
          <div className={KHOI}>
            <DaiLoi loi={ds.error} onThuLai={() => void ds.refetch()} />
          </div>
        ) : (
          <div className="grid items-start gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
            <div className={cn(KHOI, 'space-y-3 p-3 lg:sticky lg:top-0')}>
              <Input
                allowClear
                prefix={<SearchOutlined className="text-content-muted" />}
                aria-label={t('troLyAiBot.timBot')}
                placeholder={t('troLyAiBot.timBot')}
                value={tuKhoa}
                onChange={(e) => setTuKhoa(e.target.value)}
              />

              {ds.isPending ? (
                <div className="space-y-1" aria-busy="true">
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} className="h-9 rounded" />
                  ))}
                </div>
              ) : dsLoc.length ? (
                <ul aria-label={t('troLyAiBot.danhSachBot')} className="max-h-[70vh] space-y-0.5 overflow-y-auto">
                  {dsLoc.map((b) => (
                    <DongBot
                      key={b.id}
                      bot={b}
                      dangChon={chon === b.id}
                      onChon={() =>
                        chon !== b.id &&
                        roiForm(() => {
                          setTaoMoi(false);
                          setChon(b.id);
                        })
                      }
                    />
                  ))}
                </ul>
              ) : ds.data?.length ? (
                // Rỗng vì từ khoá: không mời tạo bot.
                <Empty title={t('troLyAiBot.khongTimThayBot')} className="py-6" />
              ) : (
                <Empty
                  icon={RobotOutlined}
                  className="py-6"
                  title={t('troLyAiBot.chuaCoBot')}
                  description={q.chatbotTao ? t('troLyAiBot.themBotDauTien') : t('troLyAiBot.quanTriTao')}
                />
              )}
            </div>

            {taoMoi || dangSua ? (
              <FormBot
                key={dangSua?.id ?? 'moi'}
                bot={dangSua}
                boTriThuc={(bo.data ?? []).map((c) => ({ id: c.id, ten: c.ten, phu: '' }))}
                nguonDuLieu={(nguon.data ?? []).map((s) => ({ id: s.id, ten: s.name, phu: s.status }))}
                suaDuoc={suaDuoc}
                onBan={datBan}
                onXong={(b) => {
                  setTaoMoi(false);
                  setChon(b.id);
                }}
                onHuy={() => {
                  setTaoMoi(false);
                  setChon(null);
                }}
                onXoa={dangSua && q.chatbotXoa ? () => {
                  xoa.reset();
                  setXoaBot(dangSua);
                } : undefined}
              />
            ) : (
              <div className={cn(KHOI, 'grid place-items-center p-12 text-center')}>
                <Empty icon={RobotOutlined} title={t('troLyAiBot.chonBot')} />
              </div>
            )}
          </div>
        )}
      </div>

      <HopXacNhan
        open={!!xoaBot}
        onClose={() => setXoaBot(null)}
        tieuDe={t('troLyAiBot.xacNhanXoa', { ten: xoaBot?.name ?? '' })}
        than={t('troLyAiBot.xacNhanXoaGhi')}
        nhanXacNhan={t('troLyAiBot.xoaChatbot')}
        dangChay={xoa.isPending}
        loi={xoa.error}
        onXacNhan={() =>
          xoaBot &&
          xoa.mutate(xoaBot.id, {
            onSuccess: () => {
              toast.success(t('troLyAiBot.daXoa'));
              banRef.current = false;
              setXoaBot(null);
              setChon(null);
            },
          })
        }
      />

      <HopXacNhan
        open={!!choBo}
        onClose={() => setChoBo(null)}
        tieuDe={t('troLyAiBot.boThayDoiHoi')}
        than={t('troLyAiBot.boThayDoiGhi')}
        nhanXacNhan={t('troLyAiBot.boThayDoi')}
        dangChay={false}
        onXacNhan={() => {
          banRef.current = false;
          const lam = choBo;
          setChoBo(null);
          lam?.();
        }}
      />
    </div>
  );
}

/**
 * Một dòng bot. Trạng thái là một chấm màu kèm câu giải thích trong tooltip — ba dòng chữ cho mỗi bot từng đẩy danh sách
 * xuống chỉ còn chứa được sáu bot một màn hình.
 */
function DongBot({ bot, dangChon, onChon }: { bot: Chatbot; dangChon: boolean; onChon: () => void }) {
  const { t, locale } = useT();
  const duongDan = useDuongDan();
  const tt = trangThaiDongBot(bot);
  const trangThai =
    tt === 'chay'
      ? { cham: 'bg-success-500', chu: t('troLyAiBot.dangChay') }
      : tt === 'tu_tat'
        ? { cham: 'bg-warning-500', chu: t('troLyAiBot.tatTuDong') }
        : {
            cham: 'bg-dark-300',
            chu: t('troLyAiBot.nguonDangTat', { n: soVN(bot.knowledgeBaseIds.length + bot.sourceIds.length, locale) }),
          };

  return (
    <li className={cn('group flex items-center rounded transition', dangChon ? 'bg-primary-50' : 'hover:bg-subtle')}>
      <button
        type="button"
        aria-current={dangChon ? 'true' : undefined}
        onClick={onChon}
        className="flex min-w-0 flex-1 items-center gap-2 px-2.5 py-2 text-start"
      >
        <Tooltip title={trangThai.chu} placement="left">
          <span aria-hidden className={cn('size-2 shrink-0 rounded-full', trangThai.cham)} />
        </Tooltip>
        <span className="sr-only">{trangThai.chu}</span>
        <span
          className={cn(
            'min-w-0 flex-1 truncate text-xs',
            dangChon ? 'font-semibold text-primary-700' : 'font-medium text-content-primary',
          )}
        >
          {bot.name}
        </span>
        {!bot.active ? <Badge variant="neutral">{t('troLyAiBot.tat')}</Badge> : null}
      </button>
      {/* Bot chưa trả lời được thì không có lối vào: nó sẽ mở ra một cuộc trò chuyện câm. */}
      {bot.answerable ? (
        <Tooltip title={t('troLyAiBot.moBot', { ten: bot.name })}>
          <Link
            to={duongDan(`/tro-ly-ai/tro-chuyen/${encodeURIComponent(bot.id)}`)}
            aria-label={t('troLyAiBot.moBot', { ten: bot.name })}
            className={cn(
              'me-1 rounded p-1.5 text-xs text-content-muted hover:bg-surface hover:text-primary-700 focus-visible:opacity-100',
              dangChon ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
            )}
          >
            <MessageOutlined />
          </Link>
        </Tooltip>
      ) : null}
    </li>
  );
}
